/**
 * Stripe Payment Element — the BNPL-capable card surface (P5).
 *
 * A SEPARATE component from `steps/Payment.tsx` on purpose. That file is
 * one multi-tenant component serving Square, Boulevard, sb-vault and the
 * Stripe Card Element through a single sdkState machine, submit handler
 * and mount ref. Extending it would put every one of those tenants in
 * the blast radius of this work; H22 says they keep bit-for-bit
 * behaviour. So this is new code beside it, selected per tenant, and
 * `Payment.tsx` takes zero diff — the same shape `BeYouView.tsx` already
 * uses for the Service step.
 *
 * Two things here differ from the Card Element path, and both are
 * deliberate:
 *
 * 1. DEFERRED INTENT. The Element is created with `{mode, amount,
 *    currency}` and no client secret; the PaymentIntent is created
 *    server-side at the moment the customer pays. The alternative —
 *    minting a PaymentIntent when the step opens — would create one for
 *    every abandoned cart, and would need cancel-and-recreate on every
 *    promo or pay-in-full toggle. The amount shown here is display only;
 *    the server computes what is actually charged (D68).
 *
 * 2. TWO-WAY SIZING. A Payment Element is an accordion — picking Klarna
 *    resizes the mounted node by hundreds of pixels. The Card Element
 *    portal pins the portal's height FROM its placeholder every 250ms,
 *    which would stamp that growth back down. See lib/element-portal.
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { runConfirmPaymentFlow } from '../lib/confirm-payment-flow';
import { createSlotMount } from '../lib/slot-mount';
import { setPaymentInFlight } from '../lib/payment-lock';
import { CardIcon } from '../icons';
import {
  cardSetupAgrees,
  cardSetupFor,
  saveCardRequest,
  showSaveCardBox,
  type ElementCardSetup,
} from '../lib/save-card-choice';

// Narrow Stripe surface, declared locally so the SDK import stays lazy
// and this file does not depend on Payment.tsx's own declarations.
interface StripeElementLike {
  mount(el: HTMLElement): void;
  destroy(): void;
  on(
    event: 'change' | 'ready',
    handler: (ev: { error?: { message?: string }; value?: { type?: string } }) => void,
  ): void;
}
interface StripeElementsLike {
  create(type: 'payment', options?: Record<string, unknown>): StripeElementLike;
  /** Resolves once Stripe has applied the update (stripe-js ≥ 9 types). */
  update(options: UpdateOptions): Promise<void> | void;
  submit(): Promise<{ error?: { message?: string } }>;
}
interface UpdateOptions {
  amount?: number;
  currency?: string;
  paymentMethodTypes?: string[];
  paymentMethodOptions?: { card: { setup_future_usage: 'off_session' | 'none' } };
}
interface StripeLike {
  elements(options: Record<string, unknown>): StripeElementsLike;
  confirmPayment(args: {
    elements: StripeElementsLike;
    clientSecret: string;
    confirmParams?: {
      return_url?: string;
      payment_method_data?: { billing_details: { name?: string; email?: string } };
    };
    redirect?: 'always' | 'if_required';
  }): Promise<{ error?: { message?: string }; paymentIntent?: { id: string; status: string } }>;
}

export interface StripePaymentElementProps {
  /**
   * What sits above the payment form: the title, the appointment, and the
   * order summary with the deposit-vs-pay-in-full choice and the promo
   * code (components/DepositSummary). P5.3 — this step shipped with none
   * of it, which took pay-in-full and promos away from the very tenant it
   * was built for.
   */
  header?: ComponentChildren;
  /**
   * The total changed (pay-in-full, promo) and the fresh quote has not
   * arrived. Paying now would charge the wrong amount, so Pay waits —
   * parity with the Card Element step.
   */
  quoteStale?: boolean;
  /**
   * A change to the total is in flight (promo, pay-in-full). Pay waits,
   * without the stale-quote warning — it is not stale, just not back yet.
   */
  quotePending?: boolean;
  /**
   * True from the moment Pay is pressed — BEFORE Stripe validates the
   * form, which is earlier than `onConfirmingChange` — so the parent can
   * lock what would change the amount for the whole attempt.
   */
  onBusyChange?: (busy: boolean) => void;
  /**
   * The widget session this payment belongs to — what the close guard
   * and the hold countdown ask about. See lib/payment-lock.
   */
  sessionId: string;
  publishableKey: string;
  /** Display only — the server decides what is actually charged. */
  amountCents: number;
  currency: string;
  /**
   * Creates the PaymentIntent server-side and returns its client secret.
   * Called only once the customer has committed, after
   * `elements.submit()` has validated the form.
   */
  onCreateIntent: (
    saveCard?: { accepted: boolean; policyTextSeen: string | null },
  ) => Promise<{ clientSecret: string; returnUrl?: string }>;
  /** A terminal, in-place success (card). Redirect methods never reach this. */
  onPaid: (paymentIntentId: string) => void;
  /**
   * True from the moment the customer commits until the payment settles
   * or the browser navigates away. While set, the widget must refuse to
   * close: tearing down mid-confirm destroys the Element the call is
   * running against, and the customer sees the widget vanish with a
   * charge possibly in flight.
   */
  onConfirmingChange?: (confirming: boolean) => void;
  /**
   * Leave the step. Parity with the Card Element path, which has always
   * had one — without it a customer who changes their mind has no way
   * out short of waiting for the slot hold to expire.
   */
  onBack?: () => void;
  /**
   * The booking that follows a successful payment is in flight. The
   * component cannot see that itself: it hands off to onPaid and is
   * done. Without it the Pay button re-enables the instant Stripe
   * answers, and a second click re-runs the whole flow — which with a
   * changed amount is a real second charge.
   */
  submitting?: boolean;
  /**
   * Overrides the default "Pay $X". Optional because the component
   * already knows the amount, and reaching into Payment.tsx for its
   * private formatter would mean editing the file every other tenant
   * shares (H22).
   */
  submitLabel?: string;
  /**
   * The server refused with a recoverable code before any money moved —
   * in practice CART_EXPIRED, the hold having lapsed while the customer
   * sat on this step. They need the Time step, not a red line inside a
   * form whose only other control retries the same refusal forever.
   */
  onCartExpired?: (message: string) => void;
  /**
   * Which methods the Element may offer. Pinned, never left to the
   * account's own settings: Be You already has Klarna, Affirm, Afterpay
   * and Zip active on their Stripe account, so a deferred Element with
   * no list would render financing the moment this ships — with no
   * amount floor, no server-side eligibility and no redirect handling
   * built. Those arrive in P5.3; until then this is card-only, and the
   * server pins the same list on the PaymentIntent.
   */
  paymentMethodTypes?: string[];
  /**
   * Offer to keep the client's card (D73) — the server's answer, from the
   * deposit quote. Off by default, so other users of this component (Rx
   * orders) never show the box.
   */
  saveCardOffered?: boolean;
  /** The salon's own wording for keeping a card — shown, and sent back as what was seen. */
  saveCardPolicyText?: string | null;
  /**
   * Who is paying, from the Contact step. Put on the payment method so a
   * saved card carries its holder's name: on a shared family phone the
   * Stripe customer's name is whoever booked first, and staff choosing a
   * card at checkout need to see whose it is.
   */
  billingDetails?: { name?: string; email?: string };
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

/** After this long, a still-pending Stripe update is explained to the customer (Pay stays blocked). */
const UPDATE_SLOW_MS = 4000;

export function StripePaymentElement(props: StripePaymentElementProps) {
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const portalRef = useRef<HTMLElement | null>(null);
  const stripeRef = useRef<StripeLike | null>(null);
  const elementsRef = useRef<StripeElementsLike | null>(null);
  const elementRef = useRef<StripeElementLike | null>(null);
  /**
   * Read by the unmount cleanup. A confirm in flight must survive a
   * re-render; destroying the Element underneath `confirmPayment` is the
   * teardown hazard this component is written around.
   */
  const confirmingRef = useRef(false);

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Stripe applies elements.update() asynchronously; the returned promise
  // settles when it is done (stripe-js ≥ 9). Until then the form may still
  // show the OLD amount or methods, and submitting it could confirm against
  // an intent pinned to the new ones — so Pay waits for EVERY pending
  // update. A slow one is never treated as finished: past
  // UPDATE_SLOW_MS the customer is told, and Pay stays blocked until it
  // settles. A rejected update is shown as a recoverable error.
  const [updating, setUpdating] = useState(false);
  const pendingUpdatesRef = useRef(0);
  const slowTimerRef = useRef<number | null>(null);
  function applyUpdate(options: UpdateOptions, onApplied?: () => void) {
    const elements = elementsRef.current;
    if (!elements) return;
    pendingUpdatesRef.current += 1;
    setUpdating(true);
    if (slowTimerRef.current === null) {
      slowTimerRef.current = window.setTimeout(() => {
        if (pendingUpdatesRef.current > 0) {
          setError('Updating your payment options… if this does not clear, please refresh the page.');
        }
      }, UPDATE_SLOW_MS);
    }
    const settle = (failed: boolean) => {
      pendingUpdatesRef.current = Math.max(0, pendingUpdatesRef.current - 1);
      if (failed) setError('We could not update your payment options. Please refresh the page.');
      if (pendingUpdatesRef.current === 0) {
        if (slowTimerRef.current !== null) window.clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
        if (!failed) setError((e) => (e?.startsWith('Updating your payment options') ? null : e));
        setUpdating(false);
      }
    };
    Promise.resolve(elements.update(options)).then(
      () => {
        onApplied?.();
        settle(false);
      },
      () => settle(true),
    );
  }
  // CARD SAVING (D73). The box is the client's choice; `cardSetupRef` is
  // what the Element ACTUALLY holds, set only once Stripe applied it. The
  // intent request reports the latter, so the server mints an intent the
  // Element will confirm against (a mismatch is refused by Stripe).
  const saveOffered = props.saveCardOffered === true && !!(props.saveCardPolicyText ?? '').trim();
  const [saveTicked, setSaveTicked] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const cardSetupRef = useRef<ElementCardSetup>(undefined);
  // A tick belongs to the wording it was given for — cleared when the offer
  // or the wording changes, as on the Card Element step.
  useEffect(() => {
    setSaveTicked(false);
  }, [saveOffered, props.saveCardPolicyText]);
  useEffect(() => {
    if (status !== 'ready') return;
    const want = cardSetupFor(saveTicked && saveOffered);
    // Never set, and nothing to save: leave the Element exactly as an
    // older widget built it.
    if (cardSetupRef.current === undefined && want === 'none') return;
    if (cardSetupRef.current === want) return;
    applyUpdate({ paymentMethodOptions: { card: { setup_future_usage: want } } }, () => {
      cardSetupRef.current = want;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTicked, saveOffered, status]);

  useEffect(() => {
    props.onBusyChange?.(busy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  // Mount once per key. NOT per amount: a deferred Element takes the new
  // amount through update() below, and remounting on every promo keypress
  // would throw away half-entered card details.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    // In the widget's own layout, via a <slot> — see lib/slot-mount for
    // why not a <body> portal (it could not be scrolled past or clipped).
    const mount = createSlotMount(placeholderRef.current as HTMLDivElement, 'payment-element');
    const portal = mount.node;
    portalRef.current = portal;

    (async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        if (cancelled) return;
        const stripe = (await loadStripe(props.publishableKey)) as unknown as StripeLike | null;
        if (cancelled || !stripe) {
          if (!cancelled) {
            setStatus('error');
            setError('Card payments are unavailable right now.');
          }
          return;
        }
        stripeRef.current = stripe;
        // Deferred intent: no client secret yet. The PaymentIntent does
        // not exist until the customer commits.
        // A fresh Element holds no card option, whatever an earlier one did.
        cardSetupRef.current = undefined;
        const elements = stripe.elements({
          mode: 'payment',
          amount: props.amountCents,
          currency: props.currency.toLowerCase(),
          // Explicit, never inferred from the account — see the prop's
          // note. The server pins the identical list on the intent.
          paymentMethodTypes: props.paymentMethodTypes ?? ['card'],
        });
        elementsRef.current = elements;
        const element = elements.create('payment');
        elementRef.current = element;
        element.on('change', (ev) => {
          setError(ev.error?.message ?? null);
          if (ev.value?.type) setSelectedType(ev.value.type);
        });

        element.mount(portal);
        if (cancelled) {
          element.destroy();
          return;
        }
        setStatus('ready');
      } catch {
        if (!cancelled) {
          setStatus('error');
          setError('Could not load the payment form.');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (slowTimerRef.current !== null) window.clearTimeout(slowTimerRef.current);
      slowTimerRef.current = null;
      // A confirm in flight owns the Element; tearing it down here is
      // the failure this component exists to avoid. The portal is left
      // in place and the browser reclaims it on navigation.
      if (!confirmingRef.current) {
        // Stripe's own teardown, not just DOM removal. Relying on the
        // node being garbage collected leaves the SDK holding listeners
        // and an iframe it still believes is mounted.
        try {
          elementRef.current?.destroy();
        } catch {
          // An already-destroyed Element throws; nothing to do about it.
        }
        elementRef.current = null;
        mount.remove();
        portalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.publishableKey]);

  // The amount moved (promo applied, pay-in-full toggled). Deferred mode
  // takes it without a remount, so entered details survive.
  useEffect(() => {
    if (status !== 'ready') return;
    applyUpdate({ amount: props.amountCents });
  }, [props.amountCents, status]);

  // P5.3 — the methods moved with it: switching to pay in full at $175+
  // adds Klarna / Affirm / Afterpay, switching back takes them away. Kept
  // in step with the quote, because the PaymentIntent the server creates
  // is pinned to the same list and a mismatch fails at confirm.
  const methodsKey = (props.paymentMethodTypes ?? ['card']).join(',');
  useEffect(() => {
    if (status !== 'ready') return;
    applyUpdate({ paymentMethodTypes: methodsKey.split(',') });
  }, [methodsKey, status]);

  async function handlePay(e: Event) {
    e.preventDefault();
    if (busy || updating || status !== 'ready' || props.quoteStale || props.quotePending) return;
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements) return;
    // The box and the Element must agree before money moves (see
    // cardSetupAgrees — a rejected untick would otherwise keep the card).
    if (!cardSetupAgrees(cardSetupRef.current, saveTicked && saveOffered)) {
      setError('We could not update your card-saving choice. Please refresh the page before paying.');
      return;
    }

    setBusy(true);
    setError(null);
    const billing =
      props.billingDetails?.name || props.billingDetails?.email
        ? {
            ...(props.billingDetails.name ? { name: props.billingDetails.name } : {}),
            ...(props.billingDetails.email ? { email: props.billingDetails.email } : {}),
          }
        : null;

    // The ordering — validate, lock, create, confirm — lives in
    // lib/confirm-payment-flow so it can be tested without a DOM.
    const outcome = await runConfirmPaymentFlow({
      submit: () => elements.submit(),
      // What the Element holds, read at the moment of paying.
      createIntent: () =>
        props.onCreateIntent(saveCardRequest(cardSetupRef.current, props.saveCardPolicyText ?? null)),
      confirm: ({ clientSecret, returnUrl }) =>
        stripe.confirmPayment({
          elements,
          clientSecret,
          ...(returnUrl || billing
            ? {
                confirmParams: {
                  ...(returnUrl ? { return_url: returnUrl } : {}),
                  ...(billing ? { payment_method_data: { billing_details: billing } } : {}),
                },
              }
            : {}),
          // Cards finish in place; a redirect method navigates away and
          // this promise never settles.
          redirect: 'if_required',
        }),
      setLocked: (locked) => {
        confirmingRef.current = locked;
        // Refuses the backdrop, Escape, the close button and the
        // salon's own SalonLift.close() while this is raised — all four
        // destroy the Element this confirm is running against.
        setPaymentInFlight(props.sessionId, locked);
        props.onConfirmingChange?.(locked);
      },
    });

    setBusy(false);
    switch (outcome.kind) {
      case 'paid':
        props.onPaid(outcome.paymentIntentId);
        return;
      case 'redirecting':
        // Deliberately nothing: the browser is leaving, and the widget
        // stays locked until it does.
        return;
      case 'error':
        if (outcome.code === 'CART_EXPIRED' && props.onCartExpired) {
          // Nothing was charged — the refusal happened before the
          // intent existed — so handing them back to pick a new time
          // costs a tap and is the only control that helps.
          props.onCartExpired(outcome.message);
          return;
        }
        setError(outcome.message);
        return;
      default:
        setError(outcome.message);
    }
  }

  return (
    <form class="sbw-scroll sbw-payment" onSubmit={handlePay}>
      {props.header}
      {status === 'loading' && <div class="sbw-loading">Loading payment options…</div>}
      {/* Above the payment form, not below it as on the Card Element step:
          ticking changes the Element's own setting, and keeping it out of
          the way of typing means nothing the client entered is ever in
          question. Hidden (tick kept) while Klarna, Affirm or Afterpay is
          chosen — those are never kept. Never pre-ticked (D73). */}
      {status === 'ready' && showSaveCardBox(saveOffered, selectedType) && (
        <div class="sbw-save-card">
          {/* Colours from styles.css, not inline: Be You's payment step is
              dark-on-cream with per-class colours, and inline theme tokens
              rendered its (white) ink on a cream background. */}
          <label
            class="sbw-save-card-label"
            style={{ cursor: busy || props.submitting ? 'default' : 'pointer' }}
          >
            <input
              type="checkbox"
              checked={saveTicked}
              disabled={busy || props.submitting}
              onChange={(e) => setSaveTicked((e.target as HTMLInputElement).checked)}
            />
            <span class="sbw-save-card-text">
              <span class="sbw-save-card-title">
                <CardIcon /> Check out faster next time
              </span>
              <span class="sbw-save-card-ask">Save my card for future visits</span>
            </span>
          </label>
          <p class="sbw-save-card-fine">{props.saveCardPolicyText}</p>
        </div>
      )}
      {/* The Element renders HERE, through a <slot> (lib/slot-mount): in
          the widget's own layout, so it scrolls and clips with the step
          and its height is simply whatever the chosen method needs. */}
      <div
        ref={placeholderRef}
        class="sbw-card-container"
        aria-label="Payment details"
        style={{ position: 'relative', minHeight: '48px' }}
      />
      {props.quoteStale && (
        <div class="sbw-error" role="alert">
          We couldn&rsquo;t confirm your updated total. Please refresh the page before paying, so
          you&rsquo;re charged the right amount.
        </div>
      )}
      {error && (
        <div class="sbw-error" role="alert">
          {error}
        </div>
      )}
      <div class="sbw-actionbar">
        {props.onBack && (
          <button
            type="button"
            class="sbw-btn sbw-btn-ghost"
            onClick={props.onBack}
            disabled={busy || props.submitting}
          >
            Back
          </button>
        )}
        <button
          type="submit"
          class="sbw-btn sbw-btn-primary"
          disabled={
            busy ||
            updating ||
            props.submitting ||
            status !== 'ready' ||
            props.quoteStale === true ||
            props.quotePending === true
          }
        >
          {busy || props.submitting
            ? 'Working…'
            : (props.submitLabel ?? `Pay ${formatMoney(props.amountCents, props.currency)}`)}
        </button>
      </div>
    </form>
  );
}
